import React, { useState, useEffect, useCallback } from 'react';
import PriceDisplay from './PriceDisplay';
import CustomTreePaymentForm from './CustomTreePaymentForm';

export interface OpenCheckoutDetail {
  trees: number;
  packageType: string;
}

export const OPEN_CHECKOUT_EVENT = 'wc:open-checkout';

interface ContributionCardProps {
  title: string;
  price: number;
  trees: number;
  packageType: string;
  features: string[];
  featured?: boolean;
  savingsNote?: string;
  onContribute: (trees: number, packageType: string) => void;
  isLoading?: boolean;
  ctaLabel: string;
}

const ContributionCard: React.FC<ContributionCardProps> = ({
  title,
  price,
  trees,
  packageType,
  features,
  featured = false,
  savingsNote,
  onContribute,
  isLoading = false,
  ctaLabel
}) => {
  return (
    <div className={`contribution-card ${featured ? 'featured' : ''}`}>
      <h3>{title}</h3>
      <div className="price">
        <PriceDisplay basePrice={price} className="local-price" />
      </div>
      {savingsNote && (
        <div className="savings-pill">{savingsNote}</div>
      )}
      <ul className="features">
        {features.map((feature, index) => (
          <li key={index}>{feature}</li>
        ))}
      </ul>
      <button
        className="btn btn-primary"
        onClick={() => onContribute(trees, packageType)}
        disabled={isLoading}
      >
        {isLoading ? 'Cargando...' : ctaLabel}
      </button>
      <div className="card-trust-note">🔒 Pago seguro • Confirmación inmediata</div>
    </div>
  );
};

const ContributionSection: React.FC = () => {
  const [isLoading] = useState(false);
  const [showEmbeddedCheckout, setShowEmbeddedCheckout] = useState(false);
  const [checkoutData, setCheckoutData] = useState<{trees: number, packageType: string} | null>(null);
  const [successInfo, setSuccessInfo] = useState<{trees: number, method: 'card' | 'transfer'} | null>(null);
  const [treesPlanted, setTreesPlanted] = useState<number | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch('/api/stats');
        if (response.ok) {
          const data = await response.json();
          setTreesPlanted(data.treesPlanted);
        }
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      }
    };
    fetchStats();
  }, []);

  const handleContribution = useCallback((trees: number, packageType: string) => {
    setSuccessInfo(null);
    setCheckoutData({ trees, packageType });
    setShowEmbeddedCheckout(true);

    setTimeout(() => {
      const checkoutElement = document.getElementById('embedded-checkout');
      if (checkoutElement) {
        checkoutElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }, []);

  // Other sections (hero, special offer) can open the checkout directly
  useEffect(() => {
    const onOpenCheckout = (event: Event) => {
      const detail = (event as CustomEvent<OpenCheckoutDetail>).detail;
      if (detail && detail.trees > 0) {
        handleContribution(detail.trees, detail.packageType);
      }
    };
    window.addEventListener(OPEN_CHECKOUT_EVENT, onOpenCheckout);
    return () => window.removeEventListener(OPEN_CHECKOUT_EVENT, onOpenCheckout);
  }, [handleContribution]);

  // Support /#contribute links arriving from other pages
  useEffect(() => {
    if (window.location.hash === '#contribute') {
      setTimeout(() => {
        document.getElementById('contribute')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }
  }, []);

  const handleCheckoutSuccess = (method: 'card' | 'transfer') => {
    setSuccessInfo({ trees: checkoutData?.trees || 0, method });
    setShowEmbeddedCheckout(false);
    setCheckoutData(null);

    setTimeout(() => {
      const successElement = document.getElementById('checkout-success');
      if (successElement) {
        successElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  const handleCheckoutCancel = () => {
    setShowEmbeddedCheckout(false);
    setCheckoutData(null);
  };

  const formatTreeCount = (num: number) => num.toLocaleString('es-CO');

  const checkoutTitle = checkoutData
    ? `🌳 Plantar ${checkoutData.trees} Árbol${checkoutData.trees > 1 ? 'es' : ''}`
    : '🌳 Plantar Árboles';

  return (
    <section id="contribute" className="contribute">
      <div className="container">
        <h2 className="section-title">Planta Árboles Con Nosotros</h2>
        <p className="section-subtitle">Elige cómo te gustaría contribuir a la restauración forestal en Colombia</p>

        {treesPlanted !== null && (
          <div className="social-proof-line">
            🌳 <strong>{formatTreeCount(treesPlanted)} árboles</strong> ya plantados por personas como tú
          </div>
        )}

        <div className="contribution-grid">
          <ContributionCard
            title="Árbol Individual"
            price={5}
            trees={1}
            packageType="individual"
            ctaLabel="Plantar 1 Árbol"
            features={[
              'Plantar 1 árbol nativo',
              'Coordenadas GPS proporcionadas',
              'Actualizaciones de crecimiento',
              'Certificado de plantación'
            ]}
            onContribute={handleContribution}
            isLoading={isLoading}
          />

          <ContributionCard
            title="Arboleda"
            price={20}
            trees={5}
            packageType="grove"
            ctaLabel="Plantar 5 Árboles"
            savingsNote="💰 Ahorra $5 — solo $4/árbol"
            features={[
              'Plantar 5 árboles nativos',
              'Actualizaciones trimestrales',
              'Certificado de arboleda con nombre',
              'Documentación fotográfica'
            ]}
            featured={true}
            onContribute={handleContribution}
            isLoading={isLoading}
          />

          <ContributionCard
            title="Parcela de Bosque"
            price={70}
            trees={20}
            packageType="patch"
            ctaLabel="Plantar 20 Árboles"
            savingsNote="🎯 Mejor valor — solo $3.50/árbol"
            features={[
              'Plantar 20 árboles nativos',
              'Área forestal dedicada',
              'Informes de progreso mensual',
              'Placa personalizada',
              'Visita virtual al sitio'
            ]}
            onContribute={handleContribution}
            isLoading={isLoading}
          />
        </div>

        <div className="trust-row">
          <span>🔒 Pago seguro con Stripe</span>
          <span>💳 Tarjeta o transferencia (Bancolombia / Nequi)</span>
          <span>📍 GPS de tu árbol</span>
          <span>🧾 Certificado incluido</span>
        </div>

      </div>

      {successInfo && (
        <div
          id="checkout-success"
          style={{
            maxWidth: '720px',
            margin: '3rem auto 0',
            padding: '2.5rem 2rem',
            background: '#f0f8f0',
            borderRadius: '15px',
            border: '2px solid #228B22',
            textAlign: 'center'
          }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>
            {successInfo.method === 'card' ? '🌳✅' : '🌳📩'}
          </div>
          <h2 style={{ margin: '0 0 1rem 0', color: '#228B22' }}>
            {successInfo.method === 'card' ? '¡Pago Exitoso!' : '¡Comprobante Recibido!'}
          </h2>
          <p style={{ margin: '0 0 0.75rem 0', color: '#333', fontSize: '1.05rem' }}>
            {successInfo.method === 'card'
              ? `Gracias por plantar ${successInfo.trees} árbol${successInfo.trees > 1 ? 'es' : ''} con nosotros. Tu pago fue procesado correctamente.`
              : `Gracias por plantar ${successInfo.trees} árbol${successInfo.trees > 1 ? 'es' : ''} con nosotros. Verificaremos tu transferencia en 24-48 horas.`}
          </p>
          <p style={{ margin: '0 0 1.5rem 0', color: '#666', fontSize: '0.95rem' }}>
            Recibirás un correo de confirmación con los detalles de tu orden y las instrucciones para acceder a tu portal de cliente.
          </p>
          <button
            onClick={() => setSuccessInfo(null)}
            style={{
              padding: '12px 28px',
              background: 'linear-gradient(135deg, #228B22, #32CD32)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Entendido
          </button>
        </div>
      )}

      {showEmbeddedCheckout && checkoutData && (
        <div
          id="embedded-checkout"
          style={{
            maxWidth: '720px',
            margin: '3rem auto 0',
            padding: '2rem',
            background: 'white',
            borderRadius: '15px',
            border: '2px solid #228B22',
            boxShadow: '0 15px 40px rgba(34, 139, 34, 0.15)',
            animation: 'slideDown 0.5s ease-out',
            scrollMarginTop: '90px'
          }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1.5rem'
          }}>
            <h2 style={{ margin: 0, color: '#228B22' }}>{checkoutTitle}</h2>
            <button
              onClick={handleCheckoutCancel}
              aria-label="Cerrar pago"
              style={{
                background: '#666',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                cursor: 'pointer',
                fontSize: '18px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#333'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#666'}
            >
              ×
            </button>
          </div>

          <p style={{ margin: '0 0 1.5rem 0', color: '#666', fontSize: '0.95rem', textAlign: 'center' }}>
            🔒 Completa tu contribución de forma segura — tus datos están protegidos
          </p>

          <CustomTreePaymentForm
            trees={checkoutData.trees}
            packageType={checkoutData.packageType}
            onSuccess={handleCheckoutSuccess}
            onCancel={handleCheckoutCancel}
          />
        </div>
      )}
    </section>
  );
};

export default ContributionSection;
